import {CameraOrthographic, MATRIX4, MaterialColor, ShaderDepth, ShaderScene, System} from '../../index.js';

/**
 * Creates Hypercube.js render systems.
 * @example
 * const systemrender = new SystemRender();
 *
 * // called by an update loop
 * systemrender.tick();
 */
class SystemRender extends System {

    /**
     * Stores the shader program attribute locations.
     * @type {Object.<string, number>}
     * @private
     */
    $attributelocations;

    /**
     * Stores the bias to apply to the the texture of the depth map.
     * @type {number}
     * @private
     */
    $biasdepth;

    /**
     * Stores the buffers to use by the shader program.
     * @type {Object}
     * @private
     */
    $buffers;

    /**
     * Stores the canvas element.
     * @type {HTMLCanvasElement}
     * @private
     */
    $canvas;

    /**
     * Stores the canvas context.
     * @type {WebGL2RenderingContext}
     * @private
     */
    $context;

    /**
     * Stores the type of the current mesh bound to the shader program.
     * @type {string}
     * @private
     */
    $currentmeshtype;

    /**
     * Stores the framebuffer of the depth map.
     * @type {WebGLFramebuffer}
     * @private
     */
    $framebufferdepth;

    /**
     * Stores the depth shader program.
     * @type {WebGLProgram}
     * @private
     */
    $programdepth;

    /**
     * Stores the scene shader program.
     * @type {WebGLProgram}
     * @private
     */
    $programscene;

    /**
     * Stores the size of the texture of the depth map (for both width and height sizes).
     * @type {number}
     * @private
     */
    $sizetexturedepth;

    /**
     * Stores the half-size of the cube containing the depth map.
     * @type {number}
     * @private
     */
    $sizehalvedcubedepthmap;

    /**
     * Stores the texture of the depth map.
     * @type {WebGLTexture}
     * @private
     */
    $texturedepth;

    /**
     * Stores the shader program uniform locations.
     * @type {Object.<string, WebGLUniformLocation>}
     * @private
     */
    $uniformlocations;

    /**
     * Creates a new Hypercube.js render system.
     * @param {number} [$sizehalvedcubedepthmap] The half-size of the cube containing the depth map.
     * @param {number} [$sizetexturedepth] The size of the texture of the depth map (for both width and height sizes).
     * @param {number} [$biasdepth] The bias to apply to the the texture of the depth map.
     */
    constructor($sizehalvedcubedepthmap = 32, $sizetexturedepth = 4096, $biasdepth = 0) {

        super();

        this.$attributelocations = {};
        this.$biasdepth = $biasdepth;
        this.$buffers = {};
        this.$sizehalvedcubedepthmap = $sizehalvedcubedepthmap;
        this.$sizetexturedepth = $sizetexturedepth;
        this.$uniformlocations = {};
    }

    /**
     * Creates the attributes locations to use by the shader program.
     * @param {WebGLProgram} $program The shader program.
     * @param {typeof import('../../index.js').Shader} $shader The representation of the shader.
     * @private
     */
    $createattributelocations($program, $shader) {

        Object.keys($shader.attributes).forEach(($name) => {

            this.$attributelocations[$name] = this.$context.getAttribLocation($program, $name);
        });
    }

    /**
     * Creates the buffers to use by the shader program for given mesh.
     * @param {import('../../index.js').Mesh} $mesh The reference mesh.
     * @private
     */
    $createbuffers($mesh) {

        const buffernormal = this.$context.createBuffer();
        this.$context.bindBuffer(this.$context.ARRAY_BUFFER, buffernormal);
        this.$context.bufferData(this.$context.ARRAY_BUFFER, new Float32Array($mesh.normals), this.$context.STATIC_DRAW);

        const bufferposition = this.$context.createBuffer();
        this.$context.bindBuffer(this.$context.ARRAY_BUFFER, bufferposition);
        this.$context.bufferData(this.$context.ARRAY_BUFFER, new Float32Array($mesh.positions), this.$context.STATIC_DRAW);

        const bufferindex = this.$context.createBuffer();
        this.$context.bindBuffer(this.$context.ELEMENT_ARRAY_BUFFER, bufferindex);
        this.$context.bufferData(this.$context.ELEMENT_ARRAY_BUFFER, new Uint32Array($mesh.indices), this.$context.STATIC_DRAW);

        this.$buffers[$mesh.type] = {

            'normal': buffernormal,
            'position': bufferposition,
            'index': bufferindex
        };
    }

    /**
     * Creates the depth shader program.
     * @param {typeof import('../../index.js').Shader} $shader The representation of the depth shader.
     * @private
     */
    $createprogramdepth($shader) {

        const shadervertex = this.$context.createShader(this.$context.VERTEX_SHADER);
        this.$context.shaderSource(shadervertex, $shader.sourcevertex);
        this.$context.compileShader(shadervertex);

        const shaderfragment = this.$context.createShader(this.$context.FRAGMENT_SHADER);
        this.$context.shaderSource(shaderfragment, $shader.sourcefragment);
        this.$context.compileShader(shaderfragment);

        this.$programdepth = this.$context.createProgram();
        this.$context.attachShader(this.$programdepth, shadervertex);
        this.$context.attachShader(this.$programdepth, shaderfragment);
        this.$context.linkProgram(this.$programdepth);
    }

    /**
     * Creates the scene shader program.
     * @param {typeof import('../../index.js').Shader} $shader The representation of the shader.
     * @private
     */
    $createprogramscene($shader) {

        const shadervertex = this.$context.createShader(this.$context.VERTEX_SHADER);
        this.$context.shaderSource(shadervertex, $shader.sourcevertex);
        this.$context.compileShader(shadervertex);

        const shaderfragment = this.$context.createShader(this.$context.FRAGMENT_SHADER);
        this.$context.shaderSource(shaderfragment, $shader.sourcefragment);
        this.$context.compileShader(shaderfragment);

        this.$programscene = this.$context.createProgram();
        this.$context.attachShader(this.$programscene, shadervertex);
        this.$context.attachShader(this.$programscene, shaderfragment);
        this.$context.linkProgram(this.$programscene);
    }

    /**
     * Creates the uniform locations to use by the shader program.
     * @param {WebGLProgram} $program The shader program.
     * @param {typeof import('../../index.js').Shader} $shader The representation of the shader.
     * @private
     */
    $createuniformlocations($program, $shader) {

        Object.keys($shader.uniforms).forEach(($name) => {

            this.$uniformlocations[$name] = this.$context.getUniformLocation($program, $name);
        });
    }

    /**
     * Prepares the the depth map.
     * @param {number} $width The width of the texture of the depth map.
     * @param {number} $height The height of the texture of the depth map.
     * @private
     */
    $preparedepthmap($width, $height) {

        this.$framebufferdepth = this.$context.createFramebuffer();
        this.$context.bindFramebuffer(this.$context.FRAMEBUFFER, this.$framebufferdepth);

        this.$texturedepth = this.$context.createTexture();
        this.$context.bindTexture(this.$context.TEXTURE_2D, this.$texturedepth);

        this.$context.texParameteri(this.$context.TEXTURE_2D, this.$context.TEXTURE_MIN_FILTER, this.$context.NEAREST);
        this.$context.texParameteri(this.$context.TEXTURE_2D, this.$context.TEXTURE_MAG_FILTER, this.$context.NEAREST);
        this.$context.texParameteri(this.$context.TEXTURE_2D, this.$context.TEXTURE_WRAP_S, this.$context.CLAMP_TO_EDGE);
        this.$context.texParameteri(this.$context.TEXTURE_2D, this.$context.TEXTURE_WRAP_T, this.$context.CLAMP_TO_EDGE);

        this.$context.texImage2D(this.$context.TEXTURE_2D, 0, this.$context.DEPTH_COMPONENT32F, $width, $height, 0, this.$context.DEPTH_COMPONENT, this.$context.FLOAT, null);
        this.$context.framebufferTexture2D(this.$context.FRAMEBUFFER, this.$context.DEPTH_ATTACHMENT, this.$context.TEXTURE_2D, this.$texturedepth, 0);

        this.$context.bindTexture(this.$context.TEXTURE_2D, null);
        this.$context.bindFramebuffer(this.$context.FRAMEBUFFER, null);
    }

    /**
     * Resets the canvas.
     * @param {number} $width The context viewport width.
     * @param {number} $height The context viewport height.
     * @param {vector3} $color The context background color.
     * @private
     */
    $resetcanvas($width, $height, $color) {

        const [colorred, colorgreen, colorblue] = $color;

        this.$context.clearColor(colorred, colorgreen, colorblue, 1.0);
        this.$context.clearDepth(1.0);

        this.$context.viewport(0.0, 0.0, $width, $height);
        this.$context.clear(this.$context.COLOR_BUFFER_BIT | this.$context.DEPTH_BUFFER_BIT);
    }

    /**
     * Sends an attribute to the shader program.
     * @param {typeof import('../../index.js').Shader} $shader The representation of the shader.
     * @param {string} $name The name of the attribute.
     * @param {any} $value The value of the attribute.
     * @private
     */
    $sendattribute($shader, $name, $value) {

        if (typeof $shader.attributes[$name] === 'undefined') {

            return;
        }

        const attributetype = $shader.attributes[$name];

        if (attributetype === 'vec3') {

            this.$context.bindBuffer(this.$context.ARRAY_BUFFER, $value);
            const attributelocation = this.$attributelocations[$name];
            this.$context.vertexAttribPointer(attributelocation, 3, this.$context.FLOAT, false, 0, 0);
            this.$context.enableVertexAttribArray(attributelocation);

            return;
        }
    }

    /**
     * Sends a uniform to the shader program.
     * @param {typeof import('../../index.js').Shader} $shader The representation of the shader.
     * @param {string} $name The name of the uniform.
     * @param {any} $value The value of the uniform.
     * @private
     */
    $senduniform($shader, $name, $value) {

        if (typeof $shader.uniforms[$name] === 'undefined') {

            return;
        }

        const uniformtype = $shader.uniforms[$name];

        if (uniformtype === 'float') {

            this.$context.uniform1f(this.$uniformlocations[$name], $value);

            return;
        }

        if (uniformtype === 'mat4') {

            this.$context.uniformMatrix4fv(this.$uniformlocations[$name], false, $value);

            return;
        }

        if (uniformtype === 'sampler2D') {

            this.$context.uniform1i(this.$uniformlocations[$name], $value);

            return;
        }

        if (uniformtype === 'vec3') {

            this.$context.uniform3fv(this.$uniformlocations[$name], $value);

            return;
        }
    }

    /**
     * Destroys the system.
     * @public
     */
    destroy() {

        if (typeof this.engine.deviceviewer === 'undefined') {

            return;
        }

        this.$context.getExtension('WEBGL_lose_context').loseContext();
    }

    /**
     * Starts the system.
     * @public
     */
    start() {

        if (typeof this.engine.deviceviewer === 'undefined') {

            return;
        }

        this.$canvas = this.engine.deviceviewer.element;

        this.$context = this.$canvas.getContext('webgl2', {

            'antialias': this.scene.antialiasing
        });

        this.$context.enable(this.$context.DEPTH_TEST);
        this.$context.enable(this.$context.CULL_FACE);
        this.$context.cullFace(this.$context.FRONT);

        this.$preparedepthmap(this.$sizetexturedepth, this.$sizetexturedepth);
        this.$createprogramdepth(ShaderDepth);
        this.$createprogramscene(ShaderScene);

        this.scene.getnodeswithmesh(this.scene.noderoot).forEach(($node) => {

            $node.meshes.forEach(($mesh) => {

                if (typeof this.$buffers[$mesh.type] === 'undefined') {

                    this.$createbuffers($mesh);
                }
            });
        });
    }

    /**
     * Updates the system once.
     * @param {number} $timeframe The timeframe duration (in ms).
     * @public
     */
    tick($timeframe) {

        if (typeof this.engine.deviceviewer === 'undefined') {

            return;
        }

        this.$context.bindFramebuffer(this.$context.FRAMEBUFFER, this.$framebufferdepth);

        this.$resetcanvas(this.$sizetexturedepth, this.$sizetexturedepth, [0.0, 0.0, 0.0]);

        this.$context.useProgram(this.$programdepth);

        this.$createuniformlocations(this.$programdepth, ShaderDepth);
        this.$createattributelocations(this.$programdepth, ShaderDepth);

        const camera = new CameraOrthographic('frontfar', {

            'left': - this.$sizehalvedcubedepthmap,
            'right': this.$sizehalvedcubedepthmap,
            'bottom': - this.$sizehalvedcubedepthmap,
            'top': this.$sizehalvedcubedepthmap,
            'near': - this.$sizehalvedcubedepthmap,
            'far': this.$sizehalvedcubedepthmap
        });

        const matrixcamera = MATRIX4.invert(MATRIX4.lookat(MATRIX4.identity(), this.scene.lightdirectional.direction, [0, 1, 0]));

        this.$senduniform(ShaderDepth, 'projection', camera.projection);
        this.$senduniform(ShaderDepth, 'view', matrixcamera);

        this.scene.getnodeswithmesh(this.scene.noderoot).forEach(($node) => {

            $node.meshes.forEach(($mesh) => {

                if ($mesh.shadowcasting === false) {

                    return;
                }

                this.$senduniform(ShaderDepth, 'transformation', $node.matrix);

                if ($mesh.type !== this.$currentmeshtype) {

                    if (typeof this.$buffers[$mesh.type] === 'undefined') {

                        this.$createbuffers($mesh);
                    }

                    this.$currentmeshtype = $mesh.type;

                    this.$sendattribute(ShaderDepth, 'POSITION', this.$buffers[$mesh.type].position);

                    this.$context.bindBuffer(this.$context.ELEMENT_ARRAY_BUFFER, this.$buffers[$mesh.type].index);
                }

                this.$context.drawElements(this.$context.TRIANGLES, $mesh.indices.length, this.$context.UNSIGNED_INT, 0);
            });
        });

        this.$context.bindFramebuffer(this.$context.FRAMEBUFFER, null);

        delete this.$currentmeshtype;

        const matrixdepth = MATRIX4.multiply(MATRIX4.multiply(MATRIX4.scale(MATRIX4.translate(MATRIX4.identity(), [0.5, 0.5, 0.5]), [0.5, 0.5, 0.5]), camera.projection), matrixcamera);

        this.$resetcanvas(this.$canvas.width, this.$canvas.height, this.scene.skybox.color);

        this.$context.useProgram(this.$programscene);

        this.$createuniformlocations(this.$programscene, ShaderScene);
        this.$createattributelocations(this.$programscene, ShaderScene);

        this.scene.nodecamera.camera.setaspect(this.$engine.deviceviewer.aspect);

        this.$senduniform(ShaderScene, 'biasdepth', this.$biasdepth);
        this.$senduniform(ShaderScene, 'colorfog', this.scene.fog.color);
        this.$senduniform(ShaderScene, 'colorlightambiant', this.scene.lightambiant.color);
        this.$senduniform(ShaderScene, 'colorlightdirectional', this.scene.lightdirectional.color);
        this.$senduniform(ShaderScene, 'densityfog', this.scene.fog.density);
        this.$senduniform(ShaderScene, 'directionlightdirectional', this.scene.lightdirectional.direction);
        this.$senduniform(ShaderScene, 'distancefogbegin', this.scene.fog.distancefogbegin);
        this.$senduniform(ShaderScene, 'distancefogmaximum', this.scene.fog.distancefogmaximum);
        this.$senduniform(ShaderScene, 'matrixdepth', matrixdepth);
        this.$senduniform(ShaderScene, 'projection', this.scene.nodecamera.camera.projection);
        this.$senduniform(ShaderScene, 'view', this.scene.nodecamera.matrixinverse);

        const textureunit = 0;

        this.$senduniform(ShaderScene, 'texturedepth', textureunit);
        this.$context.activeTexture(this.$context.TEXTURE0 + textureunit);
        this.$context.bindTexture(this.$context.TEXTURE_2D, this.$texturedepth);

        this.scene.getnodeswithmesh(this.scene.noderoot).forEach(($node) => {

            $node.meshes.forEach(($mesh) => {

                if ($mesh.material instanceof MaterialColor) {

                    this.$senduniform(ShaderScene, 'colormaterial', $mesh.material.color);
                }

                this.$senduniform(ShaderScene, 'transformation', $node.matrix);

                if ($mesh.type !== this.$currentmeshtype) {

                    if (typeof this.$buffers[$mesh.type] === 'undefined') {

                        this.$createbuffers($mesh);
                    }

                    this.$currentmeshtype = $mesh.type;

                    this.$sendattribute(ShaderScene, 'NORMAL', this.$buffers[$mesh.type].normal);
                    this.$sendattribute(ShaderScene, 'POSITION', this.$buffers[$mesh.type].position);

                    this.$context.bindBuffer(this.$context.ELEMENT_ARRAY_BUFFER, this.$buffers[$mesh.type].index);
                }

                this.$context.drawElements(this.$context.TRIANGLES, $mesh.indices.length, this.$context.UNSIGNED_INT, 0);
            });
        });

        this.$context.bindTexture(this.$context.TEXTURE_2D, null);

        delete this.$currentmeshtype;
    }
}

export {

    SystemRender
};

export default SystemRender;
